<template>
    <div class="code-network-page">
        <h1 class="title is-3">
            Code network explorer (beta)
        </h1>
        <p class="subtitle is-6">
            Explore code-to-code references through CodeRef attributes. Select a node to inspect details and expand the graph.<br>
            This tool is in early beta, so expect some rough edges. Please report any issues or suggestions for improvement to the development team.
        </p>

        <article
            v-if="routeError"
            class="message is-danger">
            <div class="message-body">
                {{ routeError }}
            </div>
        </article>
        <article
            v-else-if="store.errorMessage"
            class="message is-danger">
            <div class="message-body">
                {{ store.errorMessage }}
            </div>
        </article>

        <progress
            v-if="store.loading"
            class="progress is-info is-small"
            max="100">
            Loading
        </progress>

        <div class="columns is-variable is-4">
            <div class="column is-three-fifths">
                <div class="panel">
                    <div class="panel-heading">
                        <b-icon
                            icon="graph"
                            size="is-small" />
                        Network
                        <b-button
                            class="is-pulled-right undo-expand-button ml-2"
                            type="is-light"
                            size="is-small"
                            icon-left="undo"
                            :disabled="!store.canUndo"
                            @click.stop="store.undoLastExpand">
                            Undo expand
                        </b-button>
                        <span class="node-count">{{ store.nodeCount }} nodes / {{ store.edgeCount }} edges</span>
                    </div>
                    <div class="panel-block">
                        <network-visualization
                            class="network"
                            :nodes="store.graphNodes"
                            :edges="store.graphEdges"
                            :options="networkOptions"
                            @click="onNetworkClick" />
                    </div>
                </div>
                <p>
                    Click nodes to view details. Drag nodes to reposition, drag background to pan, and use mouse wheel to zoom.
                </p>
            </div>

            <div class="column is-two-fifths">
                <div class="panel display-options-panel">
                    <div class="panel-heading">
                        <b-icon
                            icon="filter"
                            size="is-small" />
                        Display options
                    </div>
                    <div class="panel-block is-block display-options-body">
                        <b-checkbox v-model="hideInvalid">
                            Hide invalid codes
                        </b-checkbox>

                        <hr>

                        <p class="is-size-7 has-text-weight-semibold">
                            Libraries
                        </p>

                        <p
                            v-if="store.libraryDisplayOptions.length === 0"
                            class="is-size-7 has-text-grey">
                            No libraries loaded yet.
                        </p>

                        <div
                            v-for="option in store.libraryDisplayOptions"
                            :key="option.library"
                            class="library-toggle-row">
                            <b-checkbox
                                :model-value="option.visible"
                                @update:model-value="onLibraryVisibilityChanged(option.library, $event)">
                                <span
                                    class="library-color-dot"
                                    :style="{ backgroundColor: option.color }" />
                                {{ option.library }} ({{ option.totalNodes }})
                            </b-checkbox>
                        </div>
                    </div>
                </div>

                <div
                    v-if="store.selectedNode"
                    class="panel">
                    <div class="panel-heading">
                        <b-icon
                            icon="book"
                            size="is-small" />
                        {{ store.selectedNode.library }} : {{ store.selectedNode.name }}
                        <b-button
                            class="is-pulled-right undo-expand-button ml-2"
                            type="is-light"
                            size="is-small"
                            @click="openSelectedItem">
                            Open item
                        </b-button>
                    </div>
                    <div class="panel-block is-block">
                        <table class="table is-striped is-narrow is-fullwidth">
                            <tbody>
                                <tr>
                                    <th>Description</th>
                                    <td>
                                        <p class="attribute-value">
                                            {{ store.selectedNode.description || '-' }}
                                        </p>
                                    </td>
                                </tr>
                                <tr
                                    v-if="store.selectedNodeAttributesLoading && !store.selectedNodeAttributesLoaded">
                                    <th>Attributes</th>
                                    <td>Loading...</td>
                                </tr>
                                <tr
                                    v-else-if="store.selectedNodeAttributesLoaded && store.selectedNodeAttributeRows.length === 0">
                                    <th>Attributes</th>
                                    <td>No attributes defined for this library.</td>
                                </tr>
                                <tr
                                    v-for="attribute in store.selectedNodeAttributeRows"
                                    :key="attribute.definitionName">
                                    <th>{{ attribute.displayName }}</th>
                                    <td>
                                        <p class="attribute-value">
                                            {{ attribute.value || '-' }}
                                        </p>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <b-button
                            type="is-link"
                            icon-left="refresh"
                            :loading="store.loading"
                            @click="refreshFromSelectedNode">
                            Reload network from this node
                        </b-button>

                        <b-button
                            v-if="store.canExpandSelectedNode"
                            type="is-info"
                            icon-left="arrow-expand"
                            :loading="store.selectedNodeExpanding"
                            @click="expandSelected">
                            Expand references
                        </b-button>
                        <p
                            v-else
                            class="expanded-note">
                            References already loaded.
                        </p>
                    </div>

                    <div
                        v-if="store.selectedOutgoing.length > 0"
                        class="panel-block is-block">
                        <p><b>Outgoing references</b></p>
                        <table class="table is-striped is-narrow is-fullwidth relationship-table">
                            <tbody>
                                <tr
                                    v-for="edge in store.selectedOutgoing"
                                    :key="`${edge.attribute}:${edge.node.key}`">
                                    <td>
                                        <router-link :to="getCodeDetailsLink(edge.node.id)">
                                            {{ edge.node.name }}
                                        </router-link>
                                        <div class="is-size-7 has-text-grey">
                                            {{ edge.node.library }}
                                        </div>
                                    </td>
                                    <td class="is-size-7">
                                        {{ edge.attribute }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        v-if="store.selectedIncoming.length > 0"
                        class="panel-block is-block">
                        <p><b>Incoming references</b></p>
                        <table class="table is-striped is-narrow is-fullwidth relationship-table">
                            <tbody>
                                <tr
                                    v-for="edge in store.selectedIncoming"
                                    :key="`${edge.attribute}:${edge.node.key}`">
                                    <td>
                                        <router-link :to="getCodeDetailsLink(edge.node.id)">
                                            {{ edge.node.name }}
                                        </router-link>
                                        <div class="is-size-7 has-text-grey">
                                            {{ edge.node.library }}
                                        </div>
                                    </td>
                                    <td class="is-size-7">
                                        {{ edge.attribute }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div
                    v-else
                    class="box">
                    Select a node to show code details.
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import NetworkVisualization from '@/shared/vis/NetworkVisualization.vue';
    import { getCodeLinkByB64Id, getCodeNetworkLink } from '@/shared/helpers/routing';
    import { encodeIdBase64 } from '@/shared/helpers/utils';
    import { useCodeNetworkStore } from '@/stores/codeNetworkStore';

    type NetworkClickEvent = {
        nodes?: Array<string | number>;
    };

    const route = useRoute();
    const router = useRouter();
    const store = useCodeNetworkStore();
    const routeError = ref<string | null>(null);

    const hideInvalid = computed<boolean>({
        get: () => !store.showInvalid,
        set: (value: boolean) => {
            store.showInvalid = !value;
        }
    });

    const networkOptions = {
        nodes: {
            shape: 'box'
        },
        edges: {
            smooth: false
        },
        layout: {
            improvedLayout: true
        },
        interaction: {
            hover: true,
            navigationButtons: true
        },
        physics: {
            enabled: true,
            solver: 'forceAtlas2Based',
            forceAtlas2Based: {
                // Middle-ground profile: reduce repulsion and increase damping for steadier layouts.
                gravitationalConstant: -80,
                centralGravity: 0.009,
                springLength: 170,
                springConstant: 0.07,
                damping: 0.66,
                avoidOverlap: 0.55
            },
            minVelocity: 0.65,
            maxVelocity: 32,
            stabilization: {
                enabled: true,
                iterations: 180,
                updateInterval: 40,
                fit: true
            }
        }
    };

    watch(
        () => route.params.dbId,
        async (value) => {
            const dbId = Number(value);

            if (!Number.isFinite(dbId)) {
                routeError.value = 'Invalid route parameter: database code ID must be numeric.';
                return;
            }

            routeError.value = null;
            await store.initializeByDbId(dbId);
            document.title = `Code network: ${dbId}`;
        },
        { immediate: true }
    );

    function onNetworkClick(event: NetworkClickEvent) {
        if (event.nodes && event.nodes.length === 1) {
            store.selectNode(String(event.nodes[0]));
            return;
        }

        store.selectNode(null);
    }

    async function expandSelected() {
        if (!store.selectedNode) {
            return;
        }

        await store.expandNode(store.selectedNode.key);
    }

    function getCodeDetailsLink(codeId: number): string {
        return getCodeLinkByB64Id(encodeIdBase64('Code', codeId));
    }

    async function openSelectedItem() {
        if (!store.selectedNode) {
            return;
        }

        await router.push(getCodeDetailsLink(store.selectedNode.id));
    }

    function onLibraryVisibilityChanged(library: string, value: unknown) {
        store.setLibraryVisible(library, Boolean(value));
    }

    async function refreshFromSelectedNode() {
        if (!store.selectedNode) {
            return;
        }

        const selectedNodeId = store.selectedNode.id;
        const currentEntryId = Number(route.params.dbId);

        if (currentEntryId === selectedNodeId) {
            await store.initializeByDbId(selectedNodeId);
            return;
        }

        await router.push(getCodeNetworkLink(selectedNodeId));
    }
</script>

<style scoped>
.code-network-page {
    padding: 0.5rem;
}

.network {
    height: 760px;
    width: 100%;
    border: none;
    margin: 5px 0;
}

.node-count {
    float: right;
    font-size: 0.9rem;
    font-weight: normal;
}

.node-name {
    margin-bottom: 0;
}

.node-library {
    margin-top: 0;
    margin-bottom: 0.75rem;
}

.attribute-value {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
}

.relationship-table {
    max-height: 260px;
    overflow-y: auto;
    display: block;
}

.expanded-note {
    margin-top: 0.5rem;
    color: #666;
    font-size: 0.9rem;
}

.display-options-body {
    max-height: 11.5rem;
    overflow: hidden;
    transition: max-height 220ms ease;
}

.display-options-panel:hover .display-options-body,
.display-options-panel:focus-within .display-options-body {
    max-height: 32rem;
    overflow-y: auto;
}

.display-options-body hr {
    margin: 0.55rem 0;
}

.undo-expand-button {
    margin-top: -0.15rem;
}

.library-toggle-row {
    margin-bottom: 0.35rem;
}

.library-color-dot {
    display: inline-block;
    width: 0.8rem;
    height: 0.8rem;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.25);
    margin-right: 0.45rem;
    vertical-align: text-bottom;
}
</style>
